/**
 * Shows where the Haiku cross-check read the screenshot differently from OCR.
 *
 * The point of the cross-check is that it does NOT silently replace the OCR
 * value — a second reader that quietly overwrote the first would throw away
 * the only signal telling the user something is wrong. So this renders both
 * numbers side by side and makes the user pick.
 *
 * Kept as its own component with a small insertion into
 * LaunchMonitorImportReview so the two files can change independently.
 */

import React from 'react';
import { FieldDisagreement, LLMCrosscheckStatus } from '../services/launchMonitorImportService';

interface Props {
  status?: LLMCrosscheckStatus | null;
  /** Keyed by API field name (_driver / _7i / _wedge1). */
  disagreements?: Record<string, FieldDisagreement>;
  /** Human label for an API field key. */
  labelFor: (apiKey: string) => string;
  /** Which value the user currently has in the form, by API field key. */
  currentValue: (apiKey: string) => number | null;
  /** Write a chosen value back into the editable form. */
  onUseValue: (apiKey: string, value: number) => void;
}

function formatValue(value: number): string {
  // Spin rates are whole numbers; angles and speeds carry a decimal. Trailing
  // zeros on a rounded value read as false precision.
  return Number(value.toFixed(2)).toString();
}

const LaunchMonitorCrossCheck: React.FC<Props> = ({
  status,
  disagreements,
  labelFor,
  currentValue,
  onUseValue,
}) => {
  // No cross-check ran. Deliberately silent rather than reassuring: a user who
  // is not on the tier, or whose read had nothing to check, has not had their
  // numbers confirmed by anything and should not be told otherwise.
  if (!status || status === 'not_entitled' || status === 'not_configured'
      || status === 'nothing_to_compare') {
    return null;
  }

  if (status === 'failed') {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-xs text-gray-600">
          The AI cross-check could not run on this image. The values below are
          from OCR only — please check them against your screen.
        </p>
      </div>
    );
  }

  const entries = Object.entries(disagreements ?? {});

  if (status === 'agreed' || entries.length === 0) {
    return (
      <div className="bg-green/10 border border-green-ink rounded-lg p-3">
        <p className="text-xs text-green-ink">
          <strong>AI cross-check agreed.</strong> A second reader saw the same
          values on this image.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
      <p className="text-xs font-semibold text-orange-900 mb-1">
        AI cross-check disagrees on {entries.length}{' '}
        {entries.length === 1 ? 'value' : 'values'}
      </p>
      <p className="text-xs text-orange-800 mb-2">
        A second reader saw something different on this image. Check these
        against your screen and pick the right one.
      </p>

      <div className="space-y-1.5">
        {entries.map(([apiKey, d]) => {
          const current = currentValue(apiKey);
          const ocrChosen = current !== null && Math.abs(current - d.ocr_value) < 1e-9;
          const llmChosen = current !== null && Math.abs(current - d.llm_value) < 1e-9;

          return (
            <div
              key={apiKey}
              className="flex flex-wrap items-center gap-2 bg-white border border-orange-100 rounded px-2 py-1.5"
            >
              <span className="text-xs font-medium text-gray-800 min-w-[110px]">
                {labelFor(apiKey)}
              </span>

              {/* A sign disagreement is called out by name: it is the failure
                  that sits inside the valid range at high confidence, so it
                  looks like nothing is wrong. */}
              {d.reason === 'sign' && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-100 rounded px-1 py-0.5">
                  sign differs
                </span>
              )}

              <button
                type="button"
                onClick={() => onUseValue(apiKey, d.ocr_value)}
                className={`text-xs rounded px-2 py-1 border ${
                  ocrChosen
                    ? 'bg-green-ink text-white border-green-ink'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                OCR read <strong>{formatValue(d.ocr_value)}</strong>
              </button>

              <button
                type="button"
                onClick={() => onUseValue(apiKey, d.llm_value)}
                className={`text-xs rounded px-2 py-1 border ${
                  llmChosen
                    ? 'bg-green-ink text-white border-green-ink'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                AI read <strong>{formatValue(d.llm_value)}</strong>
              </button>

              {!ocrChosen && !llmChosen && (
                <span className="text-[10px] text-gray-500">
                  (you edited this to {current === null ? '—' : formatValue(current)})
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LaunchMonitorCrossCheck;
